import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { finalize } from 'rxjs/operators';

import { HelpersInterface } from '@core/interfaces';
import { ErrorsHandlingService, GroupsService } from '@core/services';

const toggleArrow = trigger('toggleArrow', [
  state('none', style({ transform: 'rotate(0)' })),
  state('visible', style({ transform: 'rotate(180deg)' })),
  transition('visible => none', animate('200ms ease-in')),
  transition('none => visible', animate('200ms ease-out')),
]);

interface IScannerGroup {
  id: number;
  name: string;
  checked: boolean;
  endpoints: HelpersInterface.IGroupEndpoint[];
}

@Component({
  selector: 'app-scanner-groups',
  standalone: false,
  templateUrl: './scanner-groups.component.html',
  styleUrl: './scanner-groups.component.scss',
  animations: [toggleArrow],
})
export class ScannerGroupsComponent implements OnInit {
  @Input() public side: 'ssp' | 'dsp' = 'ssp';
  @Input() public label = '';
  @Input() public search = '';

  @Input() public set checkedGroupIds(value: number[]) {
    this._checkedGroupIds = value ?? [];
    const isSameState = this.groups.every(group => group.checked === this._checkedGroupIds.includes(group.id));

    if (!isSameState) {
      this.groups.forEach(group => group.checked = this._checkedGroupIds.includes(group.id));
      this.sortCheckedFirst();
    }
  }

  // Clicks keep the rows in place, a saved selection moves the checked groups up
  @Input() public set savedGroupIds(_: number[]) {
    this.sortCheckedFirst();
  }

  @Output() public changedItems = new EventEmitter<{ id: number; checked: boolean; endpointIds: number[]; allChecked: boolean }>();
  @Output() public loadedAllChecked = new EventEmitter<boolean>();

  protected groups: IScannerGroup[] = [];
  protected busy = false;

  private readonly destroyRef = inject(DestroyRef);

  private expandedState: Record<number, boolean> = {};
  private _checkedGroupIds: number[] = [];

  constructor(
    private groupsService: GroupsService,
    private errorsHandlingService: ErrorsHandlingService,
  ) { }

  public ngOnInit(): void {
    this.loadGroups();
  }

  // The host Select all checks every group, the search filter does not narrow it
  public selectAllGroups(checked: boolean): HelpersInterface.IScannerGroupsSelection {
    this.groups.forEach(group => group.checked = checked);

    return this.checkedSelection();
  }

  protected onToggleState(group: IScannerGroup): void {
    this.expandedState[group.id] = !this.expandedState[group.id];
  }

  protected isExpanded(groupId: number): 'visible' | 'none' {
    return this.expandedState[groupId] ? 'visible' : 'none';
  }

  // The endpoints come with the groups, so the host does not have to fetch them again
  protected onCheckedChange(checked: boolean, group: IScannerGroup): void {
    this.changedItems.emit({
      id: group.id,
      checked,
      endpointIds: this.checkedSelection().endpointIds,
      allChecked: this.isAllChecked(),
    });
  }

  protected trackByFn(_: number, group: IScannerGroup): number {
    return group.id;
  }

  private isAllChecked(): boolean {
    return Boolean(this.groups.length) && this.groups.every(group => group.checked);
  }

  private checkedSelection(): HelpersInterface.IScannerGroupsSelection {
    const checkedGroups = this.groups.filter(group => group.checked);
    const endpointIds = checkedGroups.flatMap(group => group.endpoints.map(endpoint => endpoint.id));

    return { groupIds: checkedGroups.map(group => group.id), endpointIds: [...new Set(endpointIds)] };
  }

  private sortCheckedFirst(): void {
    this.groups.sort((a, b) => Number(b.checked) - Number(a.checked));
  }

  private loadGroups(): void {
    this.busy = true;
    this.groupsService.getGroupsWithEndpoints(this.side)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.busy = false))
      .subscribe({
        next: (groups) => {
          this.groups = groups.map(group => ({
            id: group.id,
            name: group.name,
            checked: this._checkedGroupIds.includes(group.id),
            // A deleted endpoint comes back without a name
            endpoints: group.endpoint_ids
              .filter(endpoint => !!endpoint.name)
              .sort((a, b) => a.name.localeCompare(b.name)),
          }));

          this.sortCheckedFirst();
          this.loadedAllChecked.emit(this.isAllChecked());
        },
        error: (error) => this.errorsHandlingService.handleError(error),
      });
  }
}
